import {detectPlatform, PLATFORMS} from '../lib/platform-detection';
import LogoWindows from '../img/logo-windows.png';
import LogoMicrosoft from '../img/logo-microsoft.png';
import LogoMac from '../img/logo-mac.png';
import LogoIOS from '../img/logo-ios.png';
import LogoHuawei from '../img/logo-huawei.png';
import LogoAndroid from '../img/logo-android.png';
import LogoMacM1 from '../img/logo-mac-m1.png';

/**
 * Reader-installer catalogue, keyed by the platform IDs that
 * `detectPlatform()` returns. Application-level config — the
 * detection library does not own install metadata (those will move
 * to the Snapplify `app-data` repo, see roadmap → "Source install
 * URLs from app-data").
 */
const READERS = {
    [PLATFORMS.MICROSOFT_STORE]: {
        label: 'Microsoft',
        logo: LogoMicrosoft,
        url: 'https://www.microsoft.com/en-za/p/snapplify/9nrcwrhppxrj?activetab=pivot:overviewtab',
    },
    [PLATFORMS.WINDOWS_MSI]: {
        label: 'Windows',
        logo: LogoWindows,
        url: 'https://getsnapplify.com/release/Snapplify.msi',
    },
    [PLATFORMS.MACOS_ARM]: {
        label: 'Apple Silicon',
        logo: LogoMacM1,
        url: 'https://getsnapplify.com/release/Snapplify-AppleSilicon.pkg',
    },
    [PLATFORMS.MACOS_INTEL]: {
        label: 'Mac OS',
        logo: LogoMac,
        url: 'https://getsnapplify.com/release/Snapplify.pkg',
    },
    [PLATFORMS.IOS]: {
        label: 'iOS',
        logo: LogoIOS,
        url: 'https://itunes.apple.com/app/snapplify-viewer/id479343631?ls=1&mt=8&at=10lbTQ',
    },
    [PLATFORMS.ANDROID]: {
        label: 'Android',
        logo: LogoAndroid,
        url: 'https://play.google.com/store/apps/details?id=za.co.snapplify',
    },
    [PLATFORMS.HUAWEI]: {
        label: 'Huawei',
        logo: LogoHuawei,
        url: 'https://appgallery.huawei.com/app/C102670933',
    },
};

// All values rendered into the page are static config from the READERS
// table above (label / logo / url) — no user input flows through here, so
// no XSS escaping is needed at the render layer. The escapeAttr helper is
// retained so that future dynamic data (e.g. when manifests come from
// app-data) is escaped at the boundary.
const escapeAttr = (s) => String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

function renderActive(reader) {
    if (!reader) {
        return `
            <div class="reader-active reader-active--unknown">
                <div class="reader-active__copy">
                    <p class="reader-active__hello">Hello Human.</p>
                    <h1 class="reader-active__pitch">I, Snappbot, don't have a native Reader recommendation for your platform — try one of the options below.</h1>
                </div>
            </div>
        `;
    }
    return `
        <div class="reader-active">
            <a class="reader-active__link"
               href="${escapeAttr(reader.url)}"
               target="_blank"
               rel="noopener noreferrer"
               aria-label="Download Snapplify Reader for ${escapeAttr(reader.label)}">
                <img class="os-primary" src="${escapeAttr(reader.logo)}" alt=""/>
            </a>
            <div class="reader-active__copy">
                <p class="reader-active__hello">Hello Human.</p>
                <h1 class="reader-active__pitch">I, Snappbot, recommend the Snapplify Reader for <strong>${escapeAttr(reader.label)}</strong> on your device.</h1>
            </div>
        </div>
    `;
}

function renderStatic(detectedPlatform) {
    const items = Object.entries(READERS)
        .filter(([id]) => id !== detectedPlatform)
        .map(([_, reader]) => `
            <a href="${escapeAttr(reader.url)}" target="_blank" rel="noopener noreferrer">
                <img class="os-secondary" src="${escapeAttr(reader.logo)}" alt="Snapplify Reader for ${escapeAttr(reader.label)}"/>
            </a>
        `).join('');
    return `
        <div class="text-center">
            <h3 class="section_text">If I got it wrong, perhaps try one of the following devices</h3>
            <div>${items}</div>
            <h3 class="section_text"></h3>
        </div>
    `;
}

/**
 * Render the platform-detected reader recommendation + the secondary
 * download list into the supplied container element.
 */
export function renderReaderPicker(container) {
    const platform = detectPlatform();
    const reader = READERS[platform];
    container.innerHTML = renderActive(reader) + renderStatic(platform);
}
